import React from 'react';
import styled from '@emotion/styled';
import { bool, shape, string, arrayOf, number, func, object, oneOfType } from 'prop-types';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import {
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableExpandHeader,
  TableRow,
  TableExpandRow,
  TableExpandedRow,
  TableHeader,
  TableBody,
  TableCell,
  DataTableSkeleton,
  Button,
  Loading,
  SkeletonText,
  InlineNotification,
} from '@carbon/react';

import { NWComboBox, NWTextInput, AnalyticsLink } from 'Components/shared';
import ServicePagination from 'Containers/Service/ServicePagination';
import useAnalytics from 'Hooks/useAnalytics';

import IconPDF from 'Assets/images/pdf.svg';

const StyledRMAComponent = styled.div`
  .viewBtn {
    height: 32px;
    width: 32px;
    background-color: #fcfdff;
    border: 1px solid #dde6f2;
    
    svg {
      fill: #3a3a3a;
    }

    &:hover {
      background-color: #d0ddef;
    }
  }

  .errorWrapper {
    margin: 0 15px;
  }

  .cds--inline-notification {
    background: #F5F7F9;
    color: #3A3A3A;
    max-width: 100%;
    border: none;
    margin-bottom: 10px;

    &.cds--inline-notification--error {
      background: #FFF2F1;
    }

    p {
      font-size: 14px;
    }

    .cds--inline-notification__close-button .cds--inline-notification__close-icon {
      fill: #3A3A3A;
    }
  }

  .cds--data-table {
    border: 1px solid #e2e2e2;

    th {
      background-color: #f5f7f9;
    }

    td {
      background: #fff;

      .address {
        font-weight: 400;
        word-break: break-all;
      }

      button {
        background: transparent;
        border: none;
        cursor: pointer;

        > svg {
          width: 24px;
        }
      }

      span {
        font-weight: 700;
        
        &.created {
          color: #1893d0;
        }

        &.picked {
          color: #01a982;
        }

        &.received {
          color: #68a765;
        }

        &.canceled {
          color: #999;
        }
      }
    }

    tbody tr:hover {
      td {
        background: #f5f7f9;
      }

      &.cds--expandable-row td {
        background: #41618A;
      }
    }
  }

  .cds--child-row-inner-container {
    display: flex;
    gap: 0 !important;

    > div {
      flex-grow: 1;
      max-width: 50%;

      p {
        font-size: 14px;
        word-break: break-word;

        span {
          color: #707070;
        }
      }
    }
  }

  tr.cds--parent-row {
    &.cds--expandable-row {
      td {
        background: #41618A;
        color: #fff;

        a, span {
          color: #fff;
        }
      }
  
      .cds--table-expand[data-previous-value=collapsed] .cds--table-expand__svg {
        fill: #fff;
      }
  
      > td, &:hover td:first-of-type {
        border-color: transparent;
      }
  
      + tr[data-child-row] td {
        padding: 1rem;
        background: #B2C2D5;
  
        .cds--child-row-inner-container {
          background-color: #fff;
          padding: 12px;
          gap: 16px;
        }
      }
    }
  }
  
  .cds--skeleton__placeholder {
    margin-top: 14px;
    height: 42px;
    width: 100%;
  }

  @media screen and (max-width: ${ props => props.theme.device.laptop }) {
    .header {
      h1 {
        font-size: 24px;
        font-weight: 600;
      }

      .primary {
        font-size: 16px;
        border: none;
        width: auto;
        height: auto;

        span {
          font-size: 24px;
          padding-right: 5px;
        }

        &:hover {
          background-color: transparent;
        }
      }
    }

    .cds--skeleton.cds--data-table-container {
      overflow: hidden;
      max-width: calc(100vw - 34px);
    }

    .cds--data-table {
      border: none;

      > thead {
        display: none;
      }

      > tbody {
        background: #fff;

        tr {
          border: 1px solid #e2e2e2;
          position: relative;

          &:hover {
            background: #fff;
          }

          &:first-of-type {
            &:hover td {
              border: none;
            }

            td {
              border: none;
            }
          }

          &.cds--parent-row {
            display: flex;
            flex-direction: column;
            height: auto;
            margin-top: 20px;

            &.cds--expandable-row {
              .cds--table-expand {
                background: #41618A;

                + td::after {
                  content: none;
                }
              }

              td .mobileTableHeader span svg {
                stroke: #fff;
              }
            }

            td {
              border: none;

              &.cds--table-expand {
                width: auto;
                display: flex;
                justify-content: end;
                padding-right: 10px;

                .cds--table-expand__button {
                  width: 25px;
                  display: inline-block;
                }
              }

              &:first-of-type {
                height: 60px;
                background: #F5F7F9;
              }

              .actions {
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 10px;
                padding: 10px 0;

                button {
                  flex: 1;

                  span {
                    display: inline-block;
                    margin-left: 10px;
                  }
                }

                a {
                  flex: 1;

                  span {
                    display: inline-block;
                    margin-left: 10px;
                    color: #3A3A3A;
                  }
                }

                .downloads {
                  display: none;
                }
              }
            }
          }
        }
      }
    }

    .cds--child-row-inner-container {
      display: flex;
      flex-direction: column;

      > div {
        flex-grow: 1;
        max-width: 100%;
      }
    }
  }
`;

const StyledTableCell = styled(TableCell)`
  @media screen and (max-width: ${ props => props.theme.device.laptop }) {

    &.cds--table-expand {
      align-self: end;
    }

    &[data-label="id"] {
      box-shadow: none !important;
      position: absolute;
      background: transparent !important;
      top: 10px;
    }

    &[data-label="id"],
    &[data-label="awbTrackingUrl"],
    &[data-label="address"],
    &[data-label="createdAt"],
    &[data-label="status"],
    &[data-label="actions"] {    
      display: flex;
      align-items: center;
      min-height: 40px;
      padding-left: 120px;

      &:before {
        content: "${ props => props.name }:";
        position: absolute;
        left: 10px;
        width: 170px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        font-weight: 600;
      }
    }
  } 
`;

const StyledTextInputSkeleton = styled.div`
  .cds--label {
    &:before {
      background: transparent;
      transform: none;
      transition: none;
      animation: none;
      will-change: unset;
    }

    &:after {
      content: '*';
      display: inline-block;
      margin-left: 5px;
      color: ${ props => props.theme.colors.default };
    }

    background: transparent;
    display: inline-block;
    font-size: 0.875rem;
    line-height: 1rem;
    color: ${ props => props.theme.colors.darkGray };
    width: auto;
    margin: 0 !important;
  }

  margin: 1px 0 15px;
`;

const StyledButton = styled(Button)`
  width: 200px;
  height: 40px;
  padding: 0;
  justify-content: center;

  &.primary {
    border: 1px solid #1f59a2;
    color: #1f59a2;
  }

  &.danger {
    background-color: #da1e28;
    border: 1px solid transparent;
    color: #fff;
  }
`;

const StyledActions = styled.div`
  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #e2e2e2;
    padding: 16px 0;
    margin-bottom: 16px;
  }

  .form {
    border: 1px solid #e2e2e2;
    margin-bottom: 20px;

    .headerTitle {
      display: flex;
      align-items: center;
      height: 48px;
      font-size: 1.25rem;
      color: #3A3A3A;
      background-color: #F5F7F9;
      text-align: left;
      padding: 0 14px;
    }

    .content {
      padding: 1rem;
      width: 100%;
      display: grid;
      gap: 2.25rem;
      grid-template-columns: repeat(2, minmax(0px, 1fr));
    }

    .footer {
      display: flex;
      padding: 0 20px 20px;

      > button {
        margin-right: 20px;
      }
    }
  }

  @media screen and (max-width: ${ props => props.theme.device.laptop }) {
    .form {
      .content {
        grid-template-columns: auto;
        gap: 0;
        padding: 10px;
      }

      .footer {
        padding: 0 10px 20px;
        gap: 10px;

        > button {
          width: auto;
          flex: 1;
        }
      }
    }
  }
`;

const fields = {
  name: 'name',
  productName: 'productName',
  defect: 'defect',
  county: 'county',
  city: 'city',
  address: 'address',
  phone: 'phone',
  postalCode: 'postalCode',
  weight: 'weight',
  insuredValue: 'insuredValue',
};

const RMAComponent = ({
  requests,
  requestsLoading,
  pagination,
  isActionsOpen,
  setIsActionOpen,
  selectedCounty,
  counties,
  handleSelectedCounty,
  selectedCity,
  cities,
  loadingCities,
  handleSubmit,
  isRequestPending,
  handleRMAById,
  requestsById,
  requestDetailsLoading,
  postError,
  downloadAWB,
  handleCancelAddingRMA,
  validators,
  register,
  rhfErrors,
  setValue,
  control,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const analytics = useAnalytics();

  const headerData = [
    {
      key: 'id',
      header: t('pages.id'),
    },
    {
      key: 'awbTrackingUrl',
      header: t('pages.awb'),
    },
    {
      key: 'address',
      header: t('pages.address'),
    },
    {
      key: 'createdAt',
      header: t('pages.createdAt'),
    },
    {
      key: 'status',
      header: t('pages.deliveryStatus'),
    },
    {
      key: 'actions',
      header: t('pages.actions'),
    },
  ];

  const mapHeaderToLabel = label => {
    let mappedLabel = label;
    if (label === 'awbTrackingUrl') {
      mappedLabel = 'awb';
    }
    if (label === 'status') {
      mappedLabel = 'orderStatus';
    }
    return t(`pages.${mappedLabel}`);
  };

  const renderCell = cell => {
    if (cell.info.header === 'status') {
      return <span className={ cell.value }>{ t(`pages.status.${ cell.value }`) }</span>;
    }
    if (cell.info.header === 'awbTrackingUrl') {
      const { awbId, awbUrl } = cell.value;
      return awbId && (
        <AnalyticsLink
          to={ awbUrl }
          target="_blank"
          event={ { action: 'awb_clicked' } }
        >
          { awbId }
        </AnalyticsLink>
      );
    }
    if (cell.info.header === 'address') {
      return <span className="address">{ cell.value }</span>;
    }
    if (cell.info.header === 'actions') {
      const { awbId, id } = cell.value;
      return awbId && (
        <button
          type="button"
          onClick={ () => {
            downloadAWB(id);
            analytics.track('awb_downloaded');
          } }
        >
          <IconPDF />
        </button>
      );
    }

    return cell.value;
  };

  const sharedProps = fieldName => ({
    id: fieldName,
    name: fieldName,
    invalid: !!rhfErrors[fieldName]?.message.length,
    invalidText: rhfErrors[fieldName]?.message,
    ...register(fieldName, { ...validators[fieldName] }),
  });

  const sharedComboBoxProps = fieldName => ({
    id: fieldName,
    name: fieldName,
    invalid: rhfErrors[fieldName]?.message.length,
    invalidText: rhfErrors[fieldName]?.message,
    control,
    rules: { ...validators[fieldName] }
  });

  return (
    <StyledRMAComponent theme={ theme }>
      <StyledActions>
        <div className="header">
          <span></span>
          <StyledButton
            className="primary"
            kind="ghost"
            onClick={ () => setIsActionOpen(true) }
          >
            +{ t('pages.addRequest') }
          </StyledButton>
        </div>
        <InlineNotification
          kind="info"
          title={ <p>{ t('pages.infoMessage1') }<br />{ t('pages.infoMessage2') }</p> }
        />
        { isActionsOpen && (
          <div className="form">
            <div className="headerTitle">
              <h4>{ t('pages.addRequest') }</h4>
            </div>
            { postError && (
              <div className="errorWrapper">
                <InlineNotification kind="error" title={ <p>{ postError }</p> } />
              </div>
            ) }
            <div className="content">
              <section>
                <NWTextInput
                  light
                  required
                  labelText={ t(`pages.requestForm.${ fields.name }`) }
                  { ...sharedProps(fields.name) }
                />
                <NWTextInput
                  light
                  required
                  labelText={ t(`pages.requestForm.${ fields.productName }`) }
                  { ...sharedProps(fields.productName) }
                />
                <NWTextInput
                  light
                  labelText={ t(`pages.requestForm.${ fields.defect }`) }
                  { ...sharedProps(fields.defect) }
                />
                { !counties
                  ? (
                    <StyledTextInputSkeleton theme={ theme }>
                      <div className="cds--form-item">
                        <span className="cds--label cds--skeleton">
                          { t('order.dropShippingData.county') }
                        </span>
                        <div className="cds--skeleton cds--text-input" />
                      </div>
                    </StyledTextInputSkeleton>
                  ) : (
                    <NWComboBox
                      titleText={ t(`pages.requestForm.${ fields.county }`) }
                      placeholder={ `${ t('common.select') } ${ t(`pages.requestForm.${ fields.county }`) }` }
                      light
                      required
                      itemToString={ item => item?.name }
                      { ...sharedComboBoxProps(fields.county) }
                      onChange={ handleSelectedCounty }
                      items={ counties || [] }
                      initialSelectedItem={
                        (counties || []).find(county => county?.erpCountyId === selectedCounty?.erpCountyId) || ''
                      }
                    />
                  )}
                { loadingCities
                  ? (
                    <StyledTextInputSkeleton theme={ theme }>
                      <div className="cds--form-item">
                        <span className="cds--label cds--skeleton">
                          { t('order.dropShippingData.county') }
                        </span>
                        <div className="cds--skeleton cds--text-input" />
                      </div>
                    </StyledTextInputSkeleton>
                  ) : (
                    <NWComboBox
                      titleText={ t(`pages.requestForm.${ fields.city }`) }
                      placeholder={ `${ t('common.select') } ${ t(`pages.requestForm.${ fields.city }`) }` }
                      light
                      required
                      itemToString={ item => item?.name }
                      disabled={ !cities }
                      { ...sharedComboBoxProps(fields.city) }
                      onChange={ ({ selectedItem }) => setValue(fields.city, selectedItem) }
                      items={ cities || [] }
                      initialSelectedItem={
                        (cities || []).find(city => city?.id === selectedCity?.id) || ''
                      }
                    />
                  )}
                <NWTextInput
                  light
                  required
                  labelText={ t(`pages.requestForm.${ fields.address }`) }
                  { ...sharedProps(fields.address) }
                />
              </section>
              <section>
                <NWTextInput
                  light
                  required
                  labelText={ t(`pages.requestForm.${ fields.phone }`) }
                  { ...sharedProps(fields.phone) }
                />
                <NWTextInput
                  light
                  required
                  labelText={ t(`pages.requestForm.${ fields.postalCode }`) }
                  { ...sharedProps(fields.postalCode) }
                />
                <NWTextInput
                  type="number"
                  min={ 0.01 }
                  light
                  labelText={ `${t(`pages.requestForm.${ fields.weight }`)} (Kg)` }
                  { ...sharedProps(fields.weight) }
                />
                <NWTextInput
                  type="number"
                  min={ 0.01 }
                  light
                  required
                  labelText={ `${t(`pages.requestForm.${ fields.insuredValue }`)} (Lei)` }
                  { ...sharedProps(fields.insuredValue) }
                />
              </section>
            </div>
            <div className="footer">
              <StyledButton
                onClick={ handleSubmit }
                className="danger"
                kind="danger"
                disabled={ isRequestPending }
              >
                { isRequestPending && <Loading active={ isRequestPending } small withOverlay={ false } /> }
                { t('pages.save') }
              </StyledButton>
              <StyledButton
                kind="ghost"
                className="primary"
                onClick={ handleCancelAddingRMA }
              >
                { t('pages.cancel') }
              </StyledButton>
            </div>
          </div>
        )}
      </StyledActions>
      { requestsLoading ? (
        <DataTableSkeleton showToolbar={ false } showHeader={ false } />
      ) : (
        <DataTable rows={ requests } headers={ headerData }>
          {({ rows, headers, getHeaderProps, getTableProps, getRowProps }) => (
            <TableContainer>
              <Table { ...getTableProps() }>
                <TableHead>
                  <TableRow>
                    <TableExpandHeader />
                    {headers.map(header => (
                      <TableHeader { ...getHeaderProps({ header }) }>
                        {header.header}
                      </TableHeader>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map(row => (
                    <React.Fragment key={ row.id }>
                      <TableExpandRow { ...getRowProps({ row, onClick: () => handleRMAById(row.id) }) }>
                        {row.cells.map(cell => (
                          <StyledTableCell
                            data-label={ cell.info.header }
                            name={ () => mapHeaderToLabel(cell.info.header) }
                            key={ cell.id }
                          >
                            { renderCell(cell) }
                          </StyledTableCell>
                        ))}
                      </TableExpandRow>
                      <TableExpandedRow colSpan={ headers.length + 1 }>
                        { requestDetailsLoading?.[row.id] || !requestsById?.[row.id] ? <SkeletonText paragraph /> : (
                          <>
                            <div>
                              <p><span>{ t('pages.requestForm.name') }: </span> { requestsById?.[row.id]?.name }</p>
                              <p><span>{ t('pages.requestForm.productName') }: </span> { requestsById?.[row.id]?.productName }</p>
                              <p><span>{ t('pages.requestForm.defect') }: </span> { requestsById?.[row.id]?.defect || '-' }</p>
                              <p><span>{ t('pages.requestForm.county') }: </span> { requestsById?.[row.id]?.county }</p>
                              <p><span>{ t('pages.requestForm.city') }: </span> { requestsById?.[row.id]?.city }</p>
                              <p><span>{ t('pages.requestForm.phone') }: </span> { requestsById?.[row.id]?.phone }</p>
                            </div>
                            <div>
                              <p><span>{ t('pages.requestForm.address') }: </span> { requestsById?.[row.id]?.address }</p>
                              <p><span>{ t('pages.requestForm.postalCode') }: </span> { requestsById?.[row.id]?.postalCode }</p>
                              <p><span>{ t('pages.requestForm.weight') }: </span> { requestsById?.[row.id]?.weight } Kg</p>
                              <p><span>{ t('pages.requestForm.insuredValue') }: </span> { requestsById?.[row.id]?.insuredValue } Lei</p>
                            </div>
                          </>
                        )}
                      </TableExpandedRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DataTable>
      )}
      <ServicePagination loading={ requestsLoading } { ...pagination } />
    </StyledRMAComponent>
  );
};

RMAComponent.propTypes = {
  requests: arrayOf(shape({
    id: oneOfType([number, string]),
    awb: string,
    status: string,
    actions: string,
  })),
  requestsLoading: bool,
  pagination: object,
  isActionsOpen: bool,
  setIsActionOpen: func,
  selectedCounty: shape({
    erpCountyId: string,
    id: oneOfType([number, string]),
    name: string,
  }),
  counties: arrayOf(shape({
    erpCountyId: string,
    id: oneOfType([number, string]),
    name: string,
  })),
  handleSelectedCounty: func,
  selectedCity: shape({
    id: oneOfType([number, string]),
    name: string,
  }),
  cities: arrayOf(shape({
    id: oneOfType([number, string]),
    name: string,
  })),
  loadingCities: bool,
  updateForm: func,
  updateTouched: func,
  handleSubmit: func,
  isRequestPending: bool,
  handleRMAById: func,
  requestsById: object,
  requestDetailsLoading: object,
  postError: string,
  downloadAWB: func,
  handleCancelAddingRMA: func,
  validators: object,
  register: func,
  rhfErrors: object,
  setValue: func,
  control: object,
};

RMAComponent.displayName = 'RMAComponent';

export default RMAComponent;
